<script setup lang="ts">
import { computed, unref } from 'vue';
import { uniqDropDownMultiSelectSelection, uniqDropDownMultiSelectValues } from '@nova/core';

import { useCustomFieldProps } from '@satellite/composables/useCustomFieldProps';
import { useCustomFieldValidation } from '@satellite/composables/useCustomFieldValidation';

import type { CustomFieldProps } from '../types';
import CustomFieldInputBase from '../CustomFieldInputBase.vue';

/**
 * Dropdown multiselect field type - multiple select from list
 */
const {
  hideDetails = 'auto',
  displayLabel = true,
  persistentPlaceholder = true,
  required = undefined,
  ...props
} = defineProps<CustomFieldProps>();

const emit = defineEmits<{
  'update:model-value': [value: string | boolean | number | string[] | null];
  /** @deprecated Use update:model-value instead */
  // TODO: Remove this event after the migration is complete
  input: [value: string | boolean | number | string[] | null];
}>();

const { normalizedProps } = useCustomFieldProps(() => ({
  ...props,
  hideDetails,
  displayLabel,
  persistentPlaceholder,
  required
}));
const { rules } = useCustomFieldValidation(
  computed(() => ({ ...props.field, required: normalizedProps.required }))
);

const modelValueState = computed(() => uniqDropDownMultiSelectSelection(props.modelValue));

function handleInput(value: string[] | null) {
  const uniqueValue = uniqDropDownMultiSelectValues(value);
  emit('update:model-value', uniqueValue);
  emit('input', uniqueValue);
}
</script>

<template>
  <custom-field-input-base :field="field" v-bind="normalizedProps">
    <v-select
      :data-testid="testId"
      chips
      color="primary"
      density="comfortable"
      multiple
      variant="outlined"
      :hide-details="normalizedProps.hideDetails"
      :items="field.dropDownValues ?? []"
      :model-value="unref(modelValueState)"
      :persistent-placeholder="normalizedProps.persistentPlaceholder"
      :placeholder="field.placeholder"
      :readonly="normalizedProps.readOnly"
      :rules="rules"
      @update:model-value="handleInput" />
  </custom-field-input-base>
</template>
